import React, { useState, useEffect } from 'react';
import { Users, Copy, Clock, Zap, Share2, CheckCircle, ExternalLink } from 'lucide-react';
import { UniversalVideoSession } from '../../templates/universal/UniversalVideoSession';

interface DemoHostProps {
  sessionId: string;
  roomId?: string;
  hostName?: string;
}

interface DemoSession {
  sessionId: string;
  roomId: string;
  hostName: string;
  guestName: string;
  status: string;
  startTime: string;
}

export const DemoHost: React.FC<DemoHostProps> = ({ sessionId, roomId, hostName }) => {
  const [session, setSession] = useState<DemoSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [usageWarning, setUsageWarning] = useState<string | null>(null);

  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const finalRoomId = roomId || urlParams.get('roomId') || '';
  const finalHostName = hostName || urlParams.get('hostName') || 'Host';
  const isAdmin = urlParams.get('isAdmin') === 'true';
  
  console.log('[DemoHost] URL params parsed:', { 
    finalRoomId, 
    finalHostName, 
    isAdmin, 
    fullURL: window.location.href 
  });

  useEffect(() => {
    // Skip session loading and create a mock session directly
    // since we're using the universal template system
    const mockSession: DemoSession = {
      sessionId,
      roomId: finalRoomId,
      hostName: finalHostName,
      guestName: 'Guest',
      status: 'active',
      startTime: new Date().toISOString()
    };
    
    setSession(mockSession);
    setLoading(false);
    
    // Skip usage checks for admin sessions OR if we have a valid session (sessionId + roomId means session already created)
    const hasValidSession = sessionId && finalRoomId;
    if (!isAdmin && !hasValidSession) {
      checkUsageStatus();
      
      // Check usage every 30 seconds for warnings
      const interval = setInterval(checkUsageStatus, 30000);
      return () => clearInterval(interval);
    } else {
      if (isAdmin) {
        console.log('[DemoHost] Admin session detected - skipping all usage checks');
      } else if (hasValidSession) {
        console.log('[DemoHost] Valid session detected - skipping usage checks (session already created)');
      }
      return () => {}; // Return empty cleanup function
    }
  }, [sessionId, finalRoomId, finalHostName, isAdmin]);

  const checkUsageStatus = async () => {
    // Extra safety check - should never be called for admin sessions
    if (isAdmin) {
      console.log('[DemoHost] checkUsageStatus called but isAdmin=true, ignoring');
      return;
    }
    
    try {
      console.log('[DemoHost] Checking usage status for non-admin session');
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/test/rate-limit-status');
      if (response.ok) {
        const data = await response.json();
        const rateLimitCheck = data.rate_limit_check;
        
        if (!rateLimitCheck?.allowed) {
          console.log('[DemoHost] Usage limit reached, redirecting to signup');
          // Redirect to signup if limits hit
          window.location.href = rateLimitCheck?.signupUrl || 'https://app.customerconnect.live/signup';
          return;
        }

        // Show warnings when approaching limits
        const remainingMinutes = rateLimitCheck?.remainingMinutes || (rateLimitCheck?.limits?.minutes || 20);
        const remainingSessions = rateLimitCheck?.remainingSessions || (rateLimitCheck?.limits?.sessions || 25);
        
        if (remainingMinutes <= 2) {
          setUsageWarning(`⚠️ ${remainingMinutes} minutes remaining in your free trial`);
        } else if (remainingSessions <= 2) {
          setUsageWarning(`⚠️ ${remainingSessions} demo sessions remaining`);
        } else if (remainingMinutes <= 5) {
          setUsageWarning(`${remainingMinutes} minutes left in your free trial`);
        }
      }
    } catch (error) {
      console.error('Error checking usage:', error);
    }
  };

  const copyGuestLink = async () => {
    const adminParam = isAdmin ? '&isAdmin=true' : '';
    const guestUrl = `${window.location.origin}/public/demo/guest/${sessionId}?roomId=${finalRoomId}&guestName=Guest${adminParam}`;
    
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = guestUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareGuestLink = async () => {
    const adminParam = isAdmin ? '&isAdmin=true' : '';
    const guestUrl = `${window.location.origin}/public/demo/guest/${sessionId}?roomId=${finalRoomId}&guestName=Guest${adminParam}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${finalHostName}'s video call`,
          text: `${finalHostName} invited you to a video call`,
          url: guestUrl,
        });
      } catch (error) {
        console.error('Share failed:', error);
        copyGuestLink();
      }
    } else {
      copyGuestLink();
    }
  };

  const handleStartSession = () => {
    console.log('[DemoHost] Starting demo video session directly with UniversalVideoSession');
    
    // Skip all external session creation and go straight to the video session
    setSessionStarted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Demo...</h2>
          <p className="text-gray-600">Preparing your video session</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Demo Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <a
            href="/public/demo"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start New Demo
          </a>
        </div>
      </div>
    );
  }

  if (sessionStarted && session) {
    return (
      <div className="min-h-screen bg-gray-900">
        {/* Admin Mode Indicator */}
        {isAdmin && (
          <div className="fixed top-4 left-4 right-4 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center justify-between">
            <span className="text-sm font-medium">🔧 Admin Mode - Unlimited Access</span>
            <span className="text-green-100 text-sm">No time limits or session restrictions</span>
          </div>
        )}
        
        {/* Usage Warning Overlay */}
        {usageWarning && !isAdmin && (
          <div className="fixed top-4 left-4 right-4 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center justify-between">
            <span className="text-sm font-medium">{usageWarning}</span>
            <a
              href="https://app.customerconnect.live/signup"
              className="text-white hover:text-orange-100 underline text-sm font-medium"
            >
              Upgrade Now
            </a>
          </div>
        )}

        {/* Video Session */}
        <UniversalVideoSession
          roomId={finalRoomId}
          requestId={sessionId}
          agentName={finalHostName}
          participantType="host"
          customerName={session.guestName}
          sessionDetails={{
            title: 'Demo Video Call',
            category: 'demo',
            urgency: 'medium',
            description: 'Public demo session'
          }}
          isDemo={true}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {/* Usage Warning */}
      {usageWarning && (
        <div className="max-w-4xl mx-auto mb-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium text-orange-800">{usageWarning}</span>
            </div>
            <a
              href="https://app.customerconnect.live/signup"
              className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center"
            >
              Sign Up <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome, {finalHostName}! 👋
              </h1>
              <p className="text-gray-600">Your demo video room is ready</p>
            </div>
            <div className="text-right">
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                Demo Session
              </div>
            </div>
          </div>

          {/* Share Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Invite Others
            </h3>
            <p className="text-gray-600 mb-4">
              Share this link with anyone you want to join your video call:
            </p>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="text-sm font-mono text-gray-700 break-all mb-3">
                {`${window.location.origin}/public/demo/guest/${sessionId}?roomId=${finalRoomId}&guestName=Guest`}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={copyGuestLink}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </button>
                <button
                  onClick={shareGuestLink}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Start Session */}
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <Zap className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to start your demo?
          </h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Click the button below to join your video session. You can start even if your guests haven't joined yet.
          </p>
          
          <button
            onClick={handleStartSession}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg flex items-center justify-center mx-auto"
          >
            <Zap className="w-6 h-6 mr-3" />
            Start Video Session
          </button>

          {/* Demo Info */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Demo Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-blue-700">
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-2" />
                HD Video & Audio
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-2" />
                Screen Sharing
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-2" />
                Real-time Chat
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-2" />
                Mobile Friendly
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>
            Love the experience? {' '}
            <a 
              href="https://app.customerconnect.live/signup" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign up for unlimited sessions
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};